import { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Stack,
  Chip,
  Alert,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  Divider,
  Tooltip,
} from "@mui/material";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import { UrlApi } from "../../../services/apirest";
import { authHeader } from "../../../services/services";
import { ESTADOS_META, ORDEN_ESTADOS } from "./arcaEstados";

export default function ImportarArcaDialog({
  open,
  setOpen,
  idEmpresa,
  empresaNombre,
  onImported,
}) {
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [resumen, setResumen] = useState(null);
  const [desglose, setDesglose] = useState(null);
  const [error, setError] = useState(null);

  const resetear = () => {
    setFile(null);
    setResumen(null);
    setDesglose(null);
    setError(null);
    setLoading(false);
  };

  const handleClose = () => {
    if (loading) return;
    resetear();
    setOpen(false);
  };

  const handleFile = (e) => {
    setError(null);
    setResumen(null);
    setDesglose(null);
    setFile(e.target.files?.[0] ?? null);
  };

  // Desglose por estado consistente con el listado: pending por `porEstado` (/pendientes/count)
  // + registrados (/procesados). Así el resumen del import coincide con la tabla de pendientes.
  const traerDesglose = async () => {
    try {
      const [count, proc] = await Promise.all([
        fetch(UrlApi + `/comprobantes_arca/pendientes/count?id_empresa=${idEmpresa}`, {
          headers: authHeader(),
        })
          .then((r) => (r.ok ? r.json() : null))
          .catch(() => null),
        fetch(UrlApi + `/comprobantes_arca/procesados?id_empresa=${idEmpresa}`, {
          headers: authHeader(),
        })
          .then((r) => (r.ok ? r.json() : []))
          .catch(() => []),
      ]);
      const porEstado = count?.porEstado || {};
      const registrado = Array.isArray(proc) ? proc.length : 0;
      setDesglose({ registrado, ...porEstado });
    } catch {
      setDesglose(null);
    }
  };

  const handleImportar = async () => {
    if (!idEmpresa || !file) return;
    setLoading(true);
    setError(null);
    setResumen(null);
    setDesglose(null);
    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("id_empresa", idEmpresa);

      const res = await fetch(UrlApi + "/comprobantes_arca/importar", {
        method: "POST",
        headers: authHeader(),
        body: formData,
      });

      if (!res.ok) {
        // El backend devuelve { message } en los errores de validación (ej. HTTP 400
        // cuando el receptor del CSV no coincide con la empresa elegida).
        let msg = `No se pudo importar el archivo (HTTP ${res.status}).`;
        try {
          const err = await res.json();
          if (err?.message) msg = err.message;
        } catch {
          // sin cuerpo JSON: se usa el mensaje genérico
        }
        setError(msg);
        return;
      }

      const data = await res.json();
      setResumen(data);
      await traerDesglose();
      if (onImported) onImported(data);
    } catch (err) {
      console.error("Error importar ARCA:", err);
      setError("Ocurrió un error al importar el archivo. Reintentá.");
    } finally {
      setLoading(false);
    }
  };

  const conError = resumen?.conError ?? [];

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <UploadFileIcon color="primary" />
        Importar CSV de ARCA
      </DialogTitle>

      <DialogContent dividers>
        <Stack spacing={2} sx={{ pt: 0.5 }}>
          <Typography variant="body2" color="text.secondary">
            Subí el archivo CSV descargado de ARCA para{" "}
            <strong>{empresaNombre}</strong>. El sistema procesa los comprobantes
            y no duplica los ya cargados.
          </Typography>

          <Box>
            <Button
              component="label"
              variant="outlined"
              startIcon={<CloudUploadIcon />}
              disabled={loading}
              fullWidth
            >
              {file ? file.name : "Elegir archivo CSV"}
              <input
                type="file"
                accept=".csv,text/csv"
                hidden
                onChange={handleFile}
              />
            </Button>
          </Box>

          {error && <Alert severity="error">{error}</Alert>}

          {resumen && (
            <Box>
              <Divider sx={{ mb: 1.5 }} />
              <Typography variant="subtitle2" gutterBottom>
                Resumen de la importación
                {typeof resumen.totalFilas === "number" &&
                  ` (${resumen.totalFilas} filas)`}
              </Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                <Chip color="primary" label={`Importados: ${resumen.importados ?? 0}`} />
                <Chip label={`Duplicados: ${resumen.duplicados ?? 0}`} />
                <Chip
                  color={conError.length ? "error" : "default"}
                  label={`Con error: ${conError.length}`}
                />
              </Stack>

              {desglose && ORDEN_ESTADOS.some((k) => desglose[k]) && (
                <>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: "block", mt: 1.5 }}
                  >
                    Estado de los comprobantes de la empresa
                  </Typography>
                  <Stack
                    direction="row"
                    spacing={1}
                    flexWrap="wrap"
                    useFlexGap
                    sx={{ mt: 0.5 }}
                  >
                    {ORDEN_ESTADOS.filter((k) => desglose[k]).map((k) => {
                      const cfg = ESTADOS_META[k];
                      const chip = (
                        <Chip
                          key={k}
                          size="small"
                          color={cfg.color}
                          variant={cfg.variant}
                          label={`${cfg.label}: ${desglose[k]}`}
                        />
                      );
                      return cfg.tip ? (
                        <Tooltip key={k} title={cfg.tip} arrow enterDelay={0}>
                          {chip}
                        </Tooltip>
                      ) : (
                        chip
                      );
                    })}
                  </Stack>
                </>
              )}

              {(desglose?.registrado ?? resumen.registrados ?? 0) > 0 && (
                <Alert severity="success" sx={{ mt: 1.5 }}>
                  {desglose?.registrado ?? resumen.registrados} comprobantes quedaron
                  registrados (proveedores con datos completos); el resto queda pendiente
                  de revisión.
                </Alert>
              )}

              {conError.length > 0 && (
                <List dense sx={{ mt: 1, maxHeight: 180, overflow: "auto" }}>
                  {conError.map((e, i) => (
                    <ListItem key={i} divider>
                      <ListItemText
                        primary={`Fila ${e.fila ?? "?"}`}
                        secondary={e.motivo ?? "Error sin detalle"}
                      />
                    </ListItem>
                  ))}
                </List>
              )}
            </Box>
          )}
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={handleClose} disabled={loading} color="inherit">
          {resumen ? "Cerrar" : "Cancelar"}
        </Button>
        <Button
          onClick={handleImportar}
          variant="contained"
          disabled={loading || !idEmpresa || !file}
          startIcon={loading ? <CircularProgress size={18} color="inherit" /> : <UploadFileIcon />}
        >
          {loading ? "Importando…" : "Importar"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
